import React, { useEffect, useState, useContext, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import axios from 'axios';
import { toast } from 'sonner';
import { DndContext, closestCorners, PointerSensor, TouchSensor, useSensor, useSensors, useDroppable, DragOverlay } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { DateRangeFilter } from '../components/DateRangeFilter';
import { useTaskDrawer } from '../hooks/useTaskDrawer';
import { TaskDrawer } from '../components/TaskDrawer';
import { usePageHeader } from '../components/Layout';
import { Ic } from '../design/icons';
import { Avatar, StatusPill, PriChip, PriBar, Seg } from '../design/primitives';
import { statusMeta, userDisplay } from '../design/tokens';
import { useWorkspace } from '../contexts/WorkspaceContext';

const BACKEND_URL = window.location.origin;
const API = `${BACKEND_URL}/api`;

const STATUSES = [
  { id: 'beklemede', label: 'Beklemede', aliases: ['pending', 'beklemede'] },
  { id: 'işlemde', label: 'İşlemde', aliases: ['in_progress', 'işlemde'] },
  { id: 'incelemede', label: 'İncelemede', aliases: ['in_review', 'review', 'incelemede', 'revizyon_gerekli'] },
  { id: 'tamamlandı', label: 'Tamamlandı', aliases: ['completed', 'done', 'tamamlandı', 'tamamlandi'] },
];

const normalizeStatus = (status) => {
  const sl = (status || '').toLowerCase();
  for (const s of STATUSES) {
    if (s.aliases.some(a => a.toLowerCase() === sl)) return s.id;
  }
  return 'beklemede';
};

const PRIORITY_OPTIONS = [
  { value: 'low', label: 'Düşük' },
  { value: 'medium', label: 'Orta' },
  { value: 'high', label: 'Yüksek' },
  { value: 'urgent', label: 'Acil' },
];

function SortableCard({ task, canMove, onClick, unreadCount, user }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id, disabled: !canMove });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };

  const assignedName = task.assignedToName || task.assignedUser || 'Atanmamış';
  const u = userDisplay({ id: task.assignedTo || assignedName, name: assignedName });
  const isRev = task.originalStatus === 'revizyon_gerekli' || task.currentRevision;

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}
      onClick={(e) => { if (!isDragging) onClick(task.id); }}
      className="ox-surface ox-card-hover rounded-md p-3 mb-2 flex cursor-pointer"
      data-revision={isRev ? 'true' : undefined}
    >
      <PriBar p={task.priority} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 mb-1 min-w-0">
          {task.projectName && <span className="ox-mono text-[10.5px] ox-text-muted truncate min-w-0 flex-1">{task.projectName}</span>}
          {isRev && <span className="ox-rev-badge text-[10px] px-1.5 rounded ox-mono shrink-0">REV</span>}
          {unreadCount > 0 && (
            <span className="ox-mono text-[10px] px-1.5 rounded shrink-0" style={{ background: 'var(--danger)', color: '#fff' }}>{unreadCount}</span>
          )}
        </div>
        <div className="text-[13px] font-medium leading-snug mb-2 break-words" style={{ textWrap: 'pretty', overflowWrap: 'anywhere' }}>{task.title}</div>
        <div className="flex items-center gap-2 text-[11px] ox-text-muted min-w-0">
          <Avatar u={u} size={18} />
          <span className="truncate min-w-0">{assignedName.split(' ')[0]}</span>
          {task.dueDate && (
            <span className="ml-auto flex items-center gap-1 ox-mono shrink-0">
              {Ic.clock}
              {new Date(task.dueDate).toLocaleDateString('tr-TR', { day: '2-digit', month: 'short' })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function KanbanColumn({ statusKey, label, tasks, canMove, onTaskClick, unreadCounts, user, onCreateTask, canCreate }) {
  const { setNodeRef, isOver } = useDroppable({ id: statusKey });
  const st = statusMeta(statusKey);
  return (
    <div className="ox-surface rounded-md flex flex-col" style={{ minWidth: 0, outline: isOver ? '2px solid var(--accent)' : 'none', outlineOffset: -2 }}>
      <div id={statusKey} className="px-3 py-2.5 flex items-center gap-2" style={{ borderBottom: '1px solid var(--border)' }}>
        <span style={{ width: 8, height: 8, borderRadius: 2, background: st.dot }} />
        <div className="text-[12.5px] font-semibold">{label}</div>
        <span className="ox-mono text-[11px] ox-text-muted px-1.5 py-0.5 rounded" style={{ background: 'var(--surface-2)' }}>{tasks.length}</span>
        {statusKey === 'beklemede' && canCreate && (
          <button className="ml-auto ox-btn ox-btn-ghost" style={{ padding: '3px 5px' }} onClick={onCreateTask} title="Yeni görev">
            {Ic.plus}
          </button>
        )}
      </div>
      <div ref={setNodeRef} className="p-2 flex-1 overflow-y-auto overflow-x-hidden ox-scroll ox-kanban-col">
        <SortableContext items={tasks.map(t => t.id)} strategy={verticalListSortingStrategy}>
          {tasks.length === 0 ? (
            <div className="text-center text-[12px] ox-text-dim py-8">Görev yok</div>
          ) : (
            tasks.map(t => (
              <SortableCard
                key={t.id}
                task={t}
                canMove={canMove}
                onClick={onTaskClick}
                unreadCount={unreadCounts[t.id] || 0}
                user={user}
              />
            ))
          )}
        </SortableContext>
      </div>
    </div>
  );
}

export default function TaskBoard() {
  const { user } = useContext(AuthContext);
  const { activeWorkspaceId, isWorkspaceAdmin, canWriteInWorkspace } = useWorkspace();
  const location = useLocation();
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [workflows, setWorkflows] = useState([]);
  const [users, setUsers] = useState([]);
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [unreadCounts, setUnreadCounts] = useState({});

  const [searchQuery, setSearchQuery] = useState('');
  const [filterProject, setFilterProject] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [filterAssignee, setFilterAssignee] = useState('all');
  const [filterTag, setFilterTag] = useState('all');
  const [filterPeriod, setFilterPeriod] = useState('all');
  const [customStartDate, setCustomStartDate] = useState(null);
  const [customEndDate, setCustomEndDate] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [commentSearch, setCommentSearch] = useState('');
  const [commentTaskIds, setCommentTaskIds] = useState(null);

  const [viewMode, setViewMode] = useState('board');

  const [showNewTaskDialog, setShowNewTaskDialog] = useState(false);
  const initialTaskData = {
    title: '', description: '', projectId: '',
    workflowId: 'none', priority: 'medium', assignedTo: 'none',
    taskType: 'Üretim', manualStepIndex: null,
    quantityType: 'adet', quantityValue: '0', tagId: '',
  };
  const [newTaskData, setNewTaskData] = useState(initialTaskData);
  const [formErrors, setFormErrors] = useState({});
  const [touched, setTouched] = useState({});

  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [activeId, setActiveId] = useState(null);
  const { selectedTaskId, isOpen: showTaskDrawer, drawerRef, openDrawer, closeDrawer: handleCloseTaskDrawer } = useTaskDrawer();

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } })
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const taskIdParam = params.get('openTask');
    if (taskIdParam) {
      openDrawer(taskIdParam);
      navigate('/tasks', { replace: true });
    }
  }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const handler = (event) => {
      if (event.data && event.data.type === 'NAVIGATE' && event.data.taskId) {
        openDrawer(event.data.taskId);
      }
    };
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.addEventListener('message', handler);
      return () => navigator.serviceWorker.removeEventListener('message', handler);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    (async () => {
      try {
        const [p, u] = await Promise.all([fetchProjects(), fetchUsers(), fetchWorkflows(), fetchTags()]);
        await fetchTasks(p, u);
        await fetchUnreadCounts();
      } catch (e) {
        toast.error('Veriler yüklenirken bir hata oluştu');
      }
    })();
    const iv = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(iv);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProjects = async () => {
    try {
      const res = await axios.get(`${API}/projects`);
      const sorted = [...res.data].sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }));
      setProjects(sorted);
      return sorted;
    } catch { return []; }
  };

  const fetchTasks = async (projectsData = projects, usersData = users) => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/tasks`);
      const normalized = res.data.map(task => {
        const project = projectsData.find(p => p.id === task.projectId);
        const assignedUser = usersData.find(u => u.id === task.assignedTo);
        const populatedHistory = (task.assignmentHistory || []).map(h => {
          const u = usersData.find(x => x.id === h.userId);
          return { ...h, userName: u?.name || 'Bilinmeyen Kullanıcı' };
        });
        return {
          ...task,
          originalStatus: task.status,
          status: normalizeStatus(task.status),
          projectName: project?.name || '',
          assignedToName: assignedUser?.name || null,
          assignmentHistory: populatedHistory,
        };
      });
      setTasks(normalized);
    } catch {
      toast.error('Görevler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const fetchWorkflows = async () => {
    try { const r = await axios.get(`${API}/workflows`); setWorkflows(r.data); return r.data; } catch { return []; }
  };
  const fetchUsers = async () => {
    try {
      const r = await axios.get(`${API}/reports/available-users`);
      const sorted = [...r.data].sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }));
      setUsers(sorted); return sorted;
    } catch { return []; }
  };
  const fetchTags = async () => {
    try {
      const r = await axios.get(`${API}/tags`);
      const sorted = [...r.data].sort((a, b) => a.name.localeCompare(b.name, 'tr', { sensitivity: 'base' }));
      setTags(sorted); return sorted;
    } catch { return []; }
  };
  const fetchUnreadCounts = async () => {
    try { const r = await axios.get(`${API}/notifications/unread-count`); setUnreadCounts(r.data); } catch {}
  };

  const commentSearchTimer = useRef(null);
  const handleCommentSearch = useCallback((value) => {
    setCommentSearch(value);
    if (commentSearchTimer.current) clearTimeout(commentSearchTimer.current);
    if (!value || value.trim().length < 2) {
      setCommentTaskIds(null);
      return;
    }
    commentSearchTimer.current = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/comments/search`, { params: { q: value.trim() } });
        setCommentTaskIds(res.data.taskIds);
      } catch { setCommentTaskIds(null); }
    }, 400);
  }, []);

  const canDeleteTask = (task) => activeWorkspaceId
    ? isWorkspaceAdmin
    : !!user && (
        user.role === 'admin' ||
        user.role === 'super_admin' ||
        user.permissions?.includes('all') ||
        user.permissions?.includes('can_delete_tasks')
      );

  const handleDeleteTask = async () => {
    if (!taskToDelete || !canDeleteTask(taskToDelete)) {
      toast.error('Bu görevi silme yetkiniz yok');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/tasks/${taskToDelete.id}`);
      toast.success('Görev silindi');
      setShowDeleteDialog(false);
      setTaskToDelete(null);
      fetchTasks();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Silme işlemi başarısız');
    } finally {
      setDeleting(false);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!newTaskData.title.trim()) errors.title = 'Görev başlığı zorunludur';
    if (!newTaskData.projectId) errors.projectId = 'Proje seçimi zorunludur';
    const hasWorkflow = newTaskData.workflowId && newTaskData.workflowId !== 'none';
    const hasAssignee = newTaskData.assignedTo && newTaskData.assignedTo !== 'none';
    if (!hasWorkflow && !hasAssignee) errors.workflowOrAssignee = 'İş akışı veya atanan kişiden en az biri zorunludur';
    if (!hasWorkflow && hasAssignee && !newTaskData.tagId) errors.tagId = 'Direkt atamada etiket seçimi zorunludur';
    return errors;
  };

  const handleCreateTask = async (e) => {
    e.preventDefault();
    setTouched({ title: true, projectId: true, workflowOrAssignee: true, tagId: true });
    const errors = validateForm();
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) return;
    try {
      const taskData = {
        title: newTaskData.title,
        description: newTaskData.description,
        projectId: newTaskData.projectId,
        priority: newTaskData.priority,
      };
      if (newTaskData.workflowId && newTaskData.workflowId !== 'none') taskData.workflowId = newTaskData.workflowId;
      if (newTaskData.assignedTo && newTaskData.assignedTo !== 'none') {
        taskData.assignedTo = newTaskData.assignedTo;
        if (newTaskData.workflowId && newTaskData.workflowId !== 'none' && newTaskData.manualStepIndex !== null) {
          taskData.manualStepIndex = newTaskData.manualStepIndex;
          taskData.taskType = newTaskData.taskType;
        } else if (!newTaskData.workflowId || newTaskData.workflowId === 'none') {
          taskData.taskType = newTaskData.taskType;
          taskData.quantityType = newTaskData.quantityType;
          taskData.quantityValue = newTaskData.quantityValue;
          taskData.tagId = newTaskData.tagId;
        }
      }
      await axios.post(`${API}/tasks`, taskData);
      toast.success('Görev oluşturuldu');
      setShowNewTaskDialog(false);
      setNewTaskData(initialTaskData);
      setFormErrors({}); setTouched({});
      fetchTasks();
    } catch (err) {
      let msg = 'Görev oluşturulamadı';
      if (err.response?.data) {
        if (typeof err.response.data.detail === 'string') msg = err.response.data.detail;
        else if (Array.isArray(err.response.data.detail)) msg = err.response.data.detail.map(d => d.msg).join(', ');
      }
      toast.error(msg);
    }
  };

  const handleDragStart = (event) => setActiveId(event.active.id);

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    const task = tasks.find(t => t.id === active.id);
    if (!task) return;

    const normStatus = normalizeStatus(task.status);
    if (normStatus !== 'beklemede' && normStatus !== 'işlemde') return;

    let newStatus = over.id;
    const overTask = tasks.find(t => t.id === over.id);
    if (overTask) newStatus = overTask.status;

    const normNew = normalizeStatus(newStatus);
    if (normNew !== 'beklemede' && normNew !== 'işlemde') return;
    if (task.status === newStatus) return;

    let canMove = false;
    if (normStatus === 'beklemede') canMove = true;
    else canMove = user?.role === 'admin' || user?.role === 'super_admin' || user?.role === 'proje_yoneticisi';

    if (!canMove) {
      toast.error(normStatus === 'beklemede' ? 'Bu görevi taşıma yetkiniz yok' : 'Yalnızca Admin ve Proje Yöneticisi bu görevleri taşıyabilir');
      return;
    }

    setTasks(prev => prev.map(t => (t.id === active.id ? { ...t, status: newStatus } : t)));
    try {
      await axios.patch(`${API}/tasks/${active.id}`, { status: newStatus });
      toast.success('Görev durumu güncellendi');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Güncelleme başarısız');
      fetchTasks();
    }
  };

  const filteredTasks = useMemo(() => {
    return tasks.filter(task => {
      if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) return false;
      if (commentTaskIds !== null && !commentTaskIds.includes(task.id)) return false;
      if (filterProject !== 'all' && task.projectId !== filterProject) return false;
      if (filterPriority !== 'all' && task.priority !== filterPriority) return false;
      if (filterAssignee !== 'all') {
        const isAssigned = task.assignedTo === filterAssignee;
        const hasHistory = task.assignmentHistory?.some(a => a.userId === filterAssignee);
        if (!isAssigned && !hasHistory) return false;
      }
      if (filterTag !== 'all') {
        const hasTaskTag = task.tagId === filterTag;
        const hasAssignmentTag = task.assignmentHistory?.some(a => a.completedTagId === filterTag || a.tagId === filterTag);
        const taskWorkflow = task.workflowId && workflows.find(w => w.id === task.workflowId);
        const hasWorkflowTag = taskWorkflow && taskWorkflow.steps && taskWorkflow.steps.some(step => step.tagId === filterTag);
        if (!hasTaskTag && !hasAssignmentTag && !hasWorkflowTag) return false;
      }
      if (filterPeriod !== 'all') {
        const dateStr = task.updatedAt || task.createdAt;
        if (!dateStr) return false;
        const td = new Date(dateStr);
        const now = new Date();
        if (filterPeriod === 'this_month') {
          if (td < new Date(now.getFullYear(), now.getMonth(), 1)) return false;
        } else if (filterPeriod === 'prev_month') {
          const ms = new Date(now.getFullYear(), now.getMonth() - 1, 1);
          const me = new Date(now.getFullYear(), now.getMonth(), 1);
          if (td < ms || td >= me) return false;
        } else if (filterPeriod === 'this_year') {
          if (td < new Date(now.getFullYear(), 0, 1)) return false;
        } else if (filterPeriod === 'prev_year') {
          const ys = new Date(now.getFullYear() - 1, 0, 1);
          const ye = new Date(now.getFullYear(), 0, 1);
          if (td < ys || td >= ye) return false;
        } else if (filterPeriod === 'custom' && customStartDate && customEndDate) {
          const s = new Date(customStartDate); s.setHours(0, 0, 0, 0);
          const e = new Date(customEndDate); e.setHours(23, 59, 59, 999);
          if (td < s || td > e) return false;
        }
      }
      return true;
    });
  }, [tasks, workflows, searchQuery, filterProject, filterPriority, filterAssignee, filterTag, filterPeriod, customStartDate, customEndDate, commentTaskIds]);

  const tasksByStatus = useMemo(() => {
    const order = { urgent: 4, high: 3, medium: 2, low: 1 };
    const grouped = STATUSES.reduce((acc, status) => {
      const statusTasks = filteredTasks.filter(t => t.status === status.id);
      statusTasks.sort((a, b) => {
        const aRev = (a.originalStatus === 'revizyon_gerekli' || a.currentRevision) ? 1 : 0;
        const bRev = (b.originalStatus === 'revizyon_gerekli' || b.currentRevision) ? 1 : 0;
        if (bRev !== aRev) return bRev - aRev;
        if ((order[b.priority] || 0) !== (order[a.priority] || 0)) return (order[b.priority] || 0) - (order[a.priority] || 0);
        return new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt);
      });
      acc[status.id] = statusTasks;
      return acc;
    }, {});
    return grouped;
  }, [filteredTasks]);

  // In workspace context, any writer (member+admin+owner) can move tasks they own.
  // Status-specific transition restrictions are enforced separately in handleDragEnd.
  const canMoveAnyTask = activeWorkspaceId
    ? canWriteInWorkspace
    : !!user && (
        user.role === 'admin' ||
        user.role === 'super_admin' ||
        user.permissions?.includes('all') ||
        user.permissions?.includes('can_move_tasks')
      );

  // Backend create_task uses require_workspace_write in workspace context.
  const canCreateTask = activeWorkspaceId
    ? canWriteInWorkspace
    : !!user && (
        user.role === 'admin' ||
        user.role === 'super_admin' ||
        user.permissions?.includes('all') ||
        user.permissions?.includes('can_create_tasks')
      );

  const clearFilters = () => {
    setSearchQuery(''); setFilterProject('all'); setFilterPriority('all');
    setFilterAssignee('all'); setFilterTag('all'); setCommentSearch(''); setCommentTaskIds(null);
  };

  const activeFilterCount = [
    filterProject !== 'all', filterPriority !== 'all', filterAssignee !== 'all', filterTag !== 'all', commentTaskIds !== null,
  ].filter(Boolean).length;

  const overdueCount = useMemo(() => filteredTasks.filter(t => t.dueDate && new Date(t.dueDate) < new Date() && t.status !== 'tamamlandı').length, [filteredTasks]);
  const reviewCount = tasksByStatus['incelemede']?.length || 0;

  const actions = (
    <>
      <button className="ox-btn" onClick={() => fetchTasks()} title="Yenile">
        <span className={loading ? 'animate-spin' : ''}>{Ic.refresh}</span>
      </button>
      {canCreateTask && (
        <button onClick={() => { setNewTaskData(initialTaskData); setShowNewTaskDialog(true); }} className="ox-btn ox-btn-primary">
          {Ic.plus}<span>Yeni Görev</span>
        </button>
      )}
    </>
  );

  usePageHeader([{ label: 'Görevler' }], actions, [loading, user]);

  if (loading && tasks.length === 0) {
    return (
      <div className="flex items-center justify-center" style={{ minHeight: 400 }}>
        <div className="w-10 h-10 border-2 border-[var(--accent)] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="ox-page-lg space-y-4">
      {/* Header */}
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="ox-h1">Görevler</h1>
          <div className="ox-page-sub mt-1">
            <b className="ox-text-body ox-tnum">{filteredTasks.length}</b> görev
            {overdueCount > 0 && <> · <b style={{ color: 'var(--danger)' }} className="ox-tnum">{overdueCount}</b> gecikmiş</>}
            {reviewCount > 0 && <> · <b style={{ color: 'var(--review)' }} className="ox-tnum">{reviewCount}</b> incelemede</>}
          </div>
        </div>
        <Seg
          value={viewMode}
          onChange={setViewMode}
          options={[{ value: 'board', label: 'Pano' }, { value: 'list', label: 'Liste' }]}
        />
      </div>

      {/* Filter bar */}
      <div className="ox-surface rounded-md p-2 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-0 sm:min-w-[240px] max-w-full sm:max-w-sm basis-full sm:basis-auto">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 ox-text-dim">{Ic.search}</span>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Görev başlığı veya ID..."
            className="ox-input pl-9"
          />
        </div>
        <div className="relative flex-1 min-w-0 sm:min-w-[240px] max-w-full sm:max-w-sm basis-full sm:basis-auto">
          <span className="absolute left-2.5 top-1/2 -translate-y-1/2 ox-text-dim">{Ic.message}</span>
          <input
            value={commentSearch}
            onChange={(e) => handleCommentSearch(e.target.value)}
            placeholder="Yorumlarda ara..."
            className="ox-input pl-9 pr-9"
          />
          {commentSearch && (
            <button
              onClick={() => { setCommentSearch(''); setCommentTaskIds(null); }}
              className="absolute right-2 top-1/2 -translate-y-1/2 ox-text-dim"
            >{Ic.x}</button>
          )}
        </div>
        <button className={`ox-btn ${showFilters ? 'ox-btn-primary' : ''}`} onClick={() => setShowFilters(!showFilters)}>
          {Ic.filter}<span>Filtre</span>
          {activeFilterCount > 0 && (
            <span className="ox-mono text-[10px] px-1 rounded" style={{ background: 'var(--accent)', color: '#fff' }}>{activeFilterCount}</span>
          )}
        </button>
        {activeFilterCount > 0 && (
          <button className="ox-btn ox-btn-ghost" onClick={clearFilters}>{Ic.x}<span>Temizle</span></button>
        )}
      </div>

      {showFilters && (
        <div className="ox-surface rounded-md p-4">
          <div className="ox-grid-form-5">
            <div className="ox-field">
              <label>Dönem</label>
              <DateRangeFilter
                period={filterPeriod}
                onPeriodChange={setFilterPeriod}
                startDate={customStartDate}
                endDate={customEndDate}
                onStartDateChange={setCustomStartDate}
                onEndDateChange={setCustomEndDate}
                showLabel={false}
              />
            </div>
            <div className="ox-field">
              <label>Proje</label>
              <select className="ox-select" value={filterProject} onChange={e => setFilterProject(e.target.value)}>
                <option value="all">Tüm Projeler</option>
                {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </select>
            </div>
            <div className="ox-field">
              <label>Öncelik</label>
              <select className="ox-select" value={filterPriority} onChange={e => setFilterPriority(e.target.value)}>
                <option value="all">Tüm Öncelikler</option>
                {PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div className="ox-field">
              <label>Atanan</label>
              <select className="ox-select" value={filterAssignee} onChange={e => setFilterAssignee(e.target.value)}>
                <option value="all">Tüm Kullanıcılar</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </div>
            <div className="ox-field">
              <label>Etiket</label>
              <select className="ox-select" value={filterTag} onChange={e => setFilterTag(e.target.value)}>
                <option value="all">Tüm Etiketler</option>
                {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
        </div>
      )}

      {viewMode === 'board' && (
        <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="ox-grid-kanban">
            {STATUSES.map(s => (
              <KanbanColumn
                key={s.id}
                statusKey={s.id}
                label={s.label}
                tasks={tasksByStatus[s.id] || []}
                canMove={canMoveAnyTask}
                onTaskClick={openDrawer}
                unreadCounts={unreadCounts}
                user={user}
                onCreateTask={() => { setNewTaskData(initialTaskData); setShowNewTaskDialog(true); }}
                canCreate={canCreateTask}
              />
            ))}
          </div>
          <DragOverlay>
            {activeId ? (
              <div className="ox-surface rounded-md p-3" style={{ boxShadow: '0 10px 30px -10px rgba(0,0,0,.3)', border: '1px solid var(--accent)' }}>
                {(() => {
                  const t = tasks.find(x => x.id === activeId);
                  if (!t) return null;
                  return (
                    <div className="flex">
                      <PriBar p={t.priority} />
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-medium leading-snug">{t.title}</div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      )}

      {viewMode === 'list' && (
        <div className="ox-surface rounded-md overflow-hidden">
          <div className="ox-table-wrap ox-table-wide">
          <table className="w-full text-left">
            <thead>
              <tr className="text-[11px] uppercase ox-text-dim" style={{ letterSpacing: '.04em', borderBottom: '1px solid var(--border)' }}>
                <th className="py-2 pl-4 pr-2 font-medium">ID</th>
                <th className="py-2 pr-2 font-medium">Durum</th>
                <th className="py-2 pr-2 font-medium">Öncelik</th>
                <th className="py-2 pr-2 font-medium">Başlık</th>
                <th className="py-2 pr-2 font-medium">Proje</th>
                <th className="py-2 pr-2 font-medium">Atanan</th>
                <th className="py-2 pr-4 font-medium">Güncel</th>
              </tr>
            </thead>
            <tbody>
              {filteredTasks.map(t => {
                const u = userDisplay({ id: t.assignedTo || t.assignedToName, name: t.assignedToName || 'Atanmamış' });
                const isRev = t.originalStatus === 'revizyon_gerekli' || t.currentRevision;
                return (
                  <tr key={t.id} onClick={() => openDrawer(t.id)} className="ox-row-hover cursor-pointer" style={{ borderBottom: '1px solid var(--border)' }}>
                    <td className="py-2.5 pl-4 pr-2"><span className="ox-mono text-[11px] ox-text-dim">#{String(t.id).slice(-5)}</span></td>
                    <td className="py-2.5 pr-2"><StatusPill s={t.status} /></td>
                    <td className="py-2.5 pr-2"><PriChip p={t.priority} /></td>
                    <td className="py-2.5 pr-2 max-w-[420px]">
                      <div className="flex items-center gap-2">
                        <span className="text-[13px] font-medium truncate">{t.title}</span>
                        {isRev && <span className="ox-rev-badge text-[10px] px-1.5 rounded ox-mono">REV</span>}
                      </div>
                    </td>
                    <td className="py-2.5 pr-2"><span className="text-[12px] ox-text-muted truncate">{t.projectName}</span></td>
                    <td className="py-2.5 pr-2">
                      <span className="flex items-center gap-1.5 text-[12px]"><Avatar u={u} size={20} />{u.name.split(' ')[0]}</span>
                    </td>
                    <td className="py-2.5 pr-4">
                      <span className="ox-mono text-[11.5px] ox-text-dim">
                        {t.updatedAt ? new Date(t.updatedAt).toLocaleDateString('tr-TR') : '—'}
                      </span>
                    </td>
                  </tr>
                );
              })}
              {filteredTasks.length === 0 && (
                <tr><td colSpan={7} className="text-center text-[12.5px] ox-text-dim py-8">Görev yok</td></tr>
              )}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* New Task Modal */}
      {showNewTaskDialog && (
        <div className="ox-modal-backdrop" onClick={() => setShowNewTaskDialog(false)}>
          <div className="ox-modal ox-modal-lg" onClick={e => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="text-[14px] font-semibold flex items-center gap-2">{Ic.plus} Yeni Görev</div>
              <button className="ox-btn ox-btn-ghost ml-auto" onClick={() => setShowNewTaskDialog(false)}>{Ic.x}</button>
            </div>
            <form onSubmit={handleCreateTask} className="flex flex-col flex-1 overflow-hidden">
              <div className="ox-modal-body space-y-4">
                <div className="ox-field">
                  <label>Başlık <span style={{ color: 'var(--danger)' }}>*</span></label>
                  <input
                    className="ox-input"
                    value={newTaskData.title}
                    onChange={e => { setNewTaskData({ ...newTaskData, title: e.target.value }); if (touched.title) setFormErrors({ ...formErrors, title: null }); }}
                    onBlur={() => setTouched({ ...touched, title: true })}
                    placeholder="Örn: Logo tasarımı yap"
                  />
                  {formErrors.title && touched.title && <div className="text-[11.5px]" style={{ color: 'var(--danger)' }}>{formErrors.title}</div>}
                </div>

                <div className="ox-field">
                  <label>Açıklama</label>
                  <textarea
                    className="ox-textarea"
                    rows={3}
                    value={newTaskData.description}
                    onChange={e => setNewTaskData({ ...newTaskData, description: e.target.value })}
                    placeholder="Görev detayları..."
                  />
                </div>

                <div className="ox-grid-form-2">
                  <div className="ox-field">
                    <label>Proje <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <select
                      className="ox-select"
                      value={newTaskData.projectId}
                      onChange={e => { setNewTaskData({ ...newTaskData, projectId: e.target.value }); if (touched.projectId) setFormErrors({ ...formErrors, projectId: null }); }}
                      onBlur={() => setTouched({ ...touched, projectId: true })}
                    >
                      <option value="">Proje seçin</option>
                      {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                    {formErrors.projectId && touched.projectId && <div className="text-[11.5px]" style={{ color: 'var(--danger)' }}>{formErrors.projectId}</div>}
                  </div>
                  <div className="ox-field">
                    <label>Öncelik</label>
                    <select
                      className="ox-select"
                      value={newTaskData.priority}
                      onChange={e => setNewTaskData({ ...newTaskData, priority: e.target.value })}
                    >
                      {PRIORITY_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div className="ox-field">
                    <label>İş Akışı</label>
                    <select
                      className="ox-select"
                      value={newTaskData.workflowId || 'none'}
                      onChange={e => { setNewTaskData({ ...newTaskData, workflowId: e.target.value === 'none' ? '' : e.target.value }); if (touched.workflowOrAssignee) setFormErrors({ ...formErrors, workflowOrAssignee: null }); }}
                    >
                      <option value="none">İş akışı yok</option>
                      {workflows.map(w => <option key={w.id} value={w.id}>{w.name}</option>)}
                    </select>
                  </div>
                  <div className="ox-field">
                    <label>Atanan Kişi</label>
                    <select
                      className="ox-select"
                      value={newTaskData.assignedTo || 'none'}
                      onChange={e => { setNewTaskData({ ...newTaskData, assignedTo: e.target.value === 'none' ? '' : e.target.value }); if (touched.workflowOrAssignee) setFormErrors({ ...formErrors, workflowOrAssignee: null }); }}
                    >
                      <option value="none">Atanmadı</option>
                      {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </div>
                </div>

                {newTaskData.workflowId && newTaskData.workflowId !== 'none' && newTaskData.assignedTo && newTaskData.assignedTo !== 'none' && (
                  <div className="ox-field">
                    <label>İş Akışı Adımı <span style={{ color: 'var(--danger)' }}>*</span></label>
                    <select
                      className="ox-select"
                      value={newTaskData.manualStepIndex !== null ? String(newTaskData.manualStepIndex) : ''}
                      onChange={e => {
                        const idx = parseInt(e.target.value);
                        const wf = workflows.find(w => w.id === newTaskData.workflowId);
                        const stepType = (wf && !isNaN(idx)) ? (wf.steps[idx]?.stepType || 'Üretim') : 'Üretim';
                        setNewTaskData({ ...newTaskData, manualStepIndex: idx, taskType: stepType });
                      }}
                    >
                      <option value="">Adım seçiniz</option>
                      {workflows.find(w => w.id === newTaskData.workflowId)?.steps.map((step, i) => (
                        <option key={i} value={i}>{step.stepName} ({step.stepType || 'Üretim'})</option>
                      ))}
                    </select>
                  </div>
                )}

                {(!newTaskData.workflowId || newTaskData.workflowId === 'none') && newTaskData.assignedTo && newTaskData.assignedTo !== 'none' && (
                  <div className="ox-grid-form-2">
                    <div className="ox-field">
                      <label>Etiket <span style={{ color: 'var(--danger)' }}>*</span></label>
                      <select
                        className="ox-select"
                        value={newTaskData.tagId || ''}
                        onChange={e => { setNewTaskData({ ...newTaskData, tagId: e.target.value }); setFormErrors({ ...formErrors, tagId: null }); }}
                      >
                        <option value="">Etiket seçin</option>
                        {tags.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                      </select>
                      {formErrors.tagId && touched.tagId && <div className="text-[11.5px]" style={{ color: 'var(--danger)' }}>{formErrors.tagId}</div>}
                    </div>
                    <div className="ox-field">
                      <label>Görev Tipi</label>
                      <select
                        className="ox-select"
                        value={newTaskData.taskType || 'Üretim'}
                        onChange={e => setNewTaskData({ ...newTaskData, taskType: e.target.value })}
                      >
                        <option value="Üretim">Üretim</option>
                        <option value="Kontrol">Kontrol</option>
                      </select>
                    </div>
                    <div className="ox-field">
                      <label>Miktar Tipi</label>
                      <select
                        className="ox-select"
                        value={newTaskData.quantityType || 'adet'}
                        onChange={e => {
                          const dv = e.target.value === 'adet' ? '0' : '00:00:00';
                          setNewTaskData({ ...newTaskData, quantityType: e.target.value, quantityValue: dv });
                        }}
                      >
                        <option value="adet">Adet</option>
                        <option value="süre">Süre</option>
                      </select>
                    </div>
                    <div className="ox-field">
                      <label>Miktar Değeri</label>
                      <input
                        className="ox-input"
                        value={newTaskData.quantityValue || (newTaskData.quantityType === 'adet' ? '0' : '00:00:00')}
                        onChange={e => setNewTaskData({ ...newTaskData, quantityValue: e.target.value })}
                        placeholder={newTaskData.quantityType === 'adet' ? '0' : '00:00:00'}
                      />
                    </div>
                  </div>
                )}

                {formErrors.workflowOrAssignee && touched.workflowOrAssignee && (
                  <div className="text-[11.5px]" style={{ color: 'var(--danger)' }}>{formErrors.workflowOrAssignee}</div>
                )}
                <div className="text-[10.5px] ox-text-dim">* İş akışı veya atanan kişiden en az biri gerekli</div>
              </div>
              <div className="ox-modal-foot">
                <button type="button" className="ox-btn" onClick={() => setShowNewTaskDialog(false)}>İptal</button>
                <div className="ml-auto" />
                <button type="submit" className="ox-btn ox-btn-primary">{Ic.check}<span>Görev Oluştur</span></button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteDialog && (
        <div className="ox-modal-backdrop" onClick={() => setShowDeleteDialog(false)}>
          <div className="ox-modal ox-modal-sm" onClick={e => e.stopPropagation()}>
            <div className="ox-modal-head">
              <div className="text-[14px] font-semibold">Görevi Sil</div>
              <button className="ox-btn ox-btn-ghost ml-auto" onClick={() => setShowDeleteDialog(false)}>{Ic.x}</button>
            </div>
            <div className="ox-modal-body">
              <p className="text-[13px]">
                <b>{taskToDelete?.title}</b> görevini silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
              </p>
            </div>
            <div className="ox-modal-foot">
              <button className="ox-btn" onClick={() => setShowDeleteDialog(false)} disabled={deleting}>İptal</button>
              <div className="ml-auto" />
              <button className="ox-btn ox-btn-danger" onClick={handleDeleteTask} disabled={deleting}>
                {Ic.trash}<span>{deleting ? 'Siliniyor...' : 'Sil'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Task Detail Drawer */}
      <TaskDrawer
        selectedTaskId={selectedTaskId}
        isOpen={showTaskDrawer}
        drawerRef={drawerRef}
        onClose={handleCloseTaskDrawer}
        onDataChanged={() => {
          axios.get(`${API}/tasks`).then(res => {
            const normalized = res.data.map(task => {
              const project = projects.find(p => p.id === task.projectId);
              const assignedUser = users.find(u => u.id === task.assignedTo);
              const populatedHistory = (task.assignmentHistory || []).map(h => {
                const u = users.find(x => x.id === h.userId);
                return { ...h, userName: u?.name || 'Bilinmeyen Kullanıcı' };
              });
              return {
                ...task,
                originalStatus: task.status,
                status: normalizeStatus(task.status),
                projectName: project?.name || '',
                assignedToName: assignedUser?.name || null,
                assignmentHistory: populatedHistory,
              };
            });
            setTasks(normalized);
          }).catch(() => {});
        }}
        onModalOpen={() => {
          const panel = drawerRef.current;
          if (panel) panel.scrollTo({ top: 0, behavior: 'smooth' });
        }}
      />
    </div>
  );
}
